<template>
	<div>
		<v-dialog
			scrollable
			transition="dialog-bottom-transition"
			:value="isVisible"
			max-width="680"
			persistent
			@click:outside="handleClickOutside"
			@keydown.esc="handleClickOutside"
		>
			<v-card>
				<v-card-title class="pa-0">
					<v-toolbar :color="`${isDark ? '' : 'grey lighten-3'}`" flat>
						<v-toolbar-title>
							{{ candidate.name }}
						</v-toolbar-title>
						<v-spacer></v-spacer>
					</v-toolbar>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text style="padding: 1rem">
					<div
						v-if="
							feedback.instructions && feedback.instructions !== '<p><br></p>'
						"
					>
						<v-alert border="left" colored-border type="info" elevation="1">
							<div v-html="sanitize(feedback.instructions)"></div>
						</v-alert>
					</div>
					<ValidationObserver
						ref="form_new_feedback"
						tag="form"
						@submit="handleSubmit"
						id="form_new_feedback"
					>
						<div style="margin-bottom: 1rem">
							<ValidationProvider rules="required" v-slot="{ errors }">
								<v-select
									v-model="feedback.application_id"
									:items="applicationsWithJobNames"
									:item-text="
										item =>
											`${item.job_name} (${handleFormatDate({
												timestamp: item.created,
											})})`
									"
									item-value="id"
									:label="$t('label.choose-job')"
									outlined
									hide-details
									dense
									:error-messages="errors"
									@change="
										newVal =>
											handleApplicationIdChange({
												applicationId: newVal,
											})
									"
								></v-select>
							</ValidationProvider>
						</div>
						<div>
							<ValidationProvider rules="required" v-slot="{ errors }">
								<v-select
									v-model="feedback.pipeline_status"
									:items="statuses"
									:item-text="
										item => {
											if (item.text) {
												return $t(`pipeline.steps.${item.text}`);
											}
											return item;
										}
									"
									item-value="value"
									:label="$t('label.status')"
									outlined
									hide-details
									dense
									:error-messages="errors"
								></v-select>
							</ValidationProvider>
						</div>
						<div style="margin: 1rem 0">
							<v-divider></v-divider>
						</div>
						<DynamicForm
							:jsonSchema="feedback.json_schema"
							:contextSchema="feedback.context_schema"
							:onChange="handleDynamicFormChange"
						></DynamicForm>
						<div>
							<fieldset class="talentics-fieldset talentics-fieldset-paddings">
								<legend>
									{{ $t('text.overall-score-for-candidate') }}
								</legend>
								<ValidationProvider rules="required" v-slot="{ errors }">
									<v-radio-group
										v-model="feedback.overall_score"
										:error-messages="errors"
										hide-details
										class="overall-score-selection-controls"
									>
										<v-radio :value="4">
											<template v-slot:label>
												<div>
													<strong style="color: #2e7d32"
														><v-icon style="color: #2e7d32">thumb_up</v-icon>
														{{ $t('text.strong-yes') }}</strong
													>
												</div>
											</template>
										</v-radio>
										<v-radio :value="3">
											<template v-slot:label>
												<div>
													<strong style="color: #66bb6a"
														><v-icon style="color: #66bb6a">thumb_up</v-icon>
														{{ $t('text.yes') }}</strong
													>
												</div>
											</template>
										</v-radio>
										<v-radio :value="5">
											<template v-slot:label>
												<div>
													<strong style="color: #9e9e9e"
														><v-icon style="color: #9e9e9e">sentiment_neutral</v-icon>
														{{ $t('text.neutral') }}</strong
													>
												</div>
											</template>
										</v-radio>
										<v-radio :value="2">
											<template v-slot:label>
												<div>
													<strong style="color: #f44336"
														><v-icon style="color: #f44336">thumb_down</v-icon>
														{{ $t('text.no') }}</strong
													>
												</div>
											</template>
										</v-radio>
										<v-radio :value="1">
											<template v-slot:label>
												<div>
													<strong style="color: #b71c1c"
														><v-icon style="color: #b71c1c">thumb_down</v-icon>
														{{ $t('text.strong-no') }}</strong
													>
												</div>
											</template>
										</v-radio>
									</v-radio-group>
								</ValidationProvider>
							</fieldset>
						</div>
					</ValidationObserver>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions class="justify-end flex-wrap" style="row-gap: 8px;">
					<v-btn text @click="() => onClose({ needsRefresh: false })">{{
						$t('button.close')
					}}</v-btn>
					<v-btn
						v-if="feedback.status === 'draft'"
						@click="handleSaveAsDraft"
						color="secondary"
					>
						<v-icon left>note_alt</v-icon>
						{{ $t('button.save-as-draft') }}
					</v-btn>
					<v-btn
						v-if="feedback.status === 'draft'"
						@click="handleCompleteClick"
						color="primary"
					>
						<v-icon left>task</v-icon>
						{{ $t('button.complete') }}
					</v-btn>
					<v-btn
						v-if="feedback.status === 'completed'"
						@click="evt => handleSubmit(evt, false)"
						color="primary"
					>
						<v-icon left>task</v-icon>
						{{ $t('button.save') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<CompleteConfirmDialog
			v-model="isCompleteConfirmVisible"
			@share="handleConfirmedComplete(true)"
			@dont-share="handleConfirmedComplete(false)"
		/>
	</div>
</template>

<script>
import { mapState } from 'vuex';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { format } from 'date-fns';
import DynamicForm from '@/components/shared/dynamicForm/DynamicForm';
import CompleteConfirmDialog from '@/components/shared/dialogs/completeConfirm/CompleteConfirmDialog';
import { sanitize } from '@/utils/sanitize';

export default {
	name: 'EditFeedbackDialog',
	components: {
		ValidationObserver,
		ValidationProvider,
		DynamicForm,
		CompleteConfirmDialog,
	},
	computed: {
		...mapState({
			isDark: state => state.currentUser.isDark,
			jobs: state => state.jobs.items,
			pipelineInterviewItems: state => state.pipelineInterview.items,
			pipelineLeadItems: state => state.pipelineLead.items,
			pipelineArchiveItems: state => state.pipelineArchive.items,
		}),
		applicationsWithJobNames() {
			const { applications } = this.candidate;
			const { jobs } = this;
			if (applications) {
				return applications.map(application => {
					const job = jobs.find(item => item.id === application.job_id);
					return {
						...application,
						job_name: job ? job.name : application.job_id,
					};
				});
			}
			return [];
		},
		statuses() {
			const {
				pipelineInterviewItems,
				pipelineLeadItems,
				pipelineArchiveItems,
			} = this;
			return [
				{ header: 'Lead' },
				...pipelineLeadItems.map(item => ({ text: item, value: item })),
				{ divider: true },
				{ header: 'Application' },
				{ text: 'new-applicant', value: 'new-applicant' },
				{ divider: true },
				{ header: 'Interview' },
				...pipelineInterviewItems.map(item => ({
					text: item,
					value: item,
				})),
				{ header: 'Archive' },
				...pipelineArchiveItems.map(item => ({
					text: item,
					value: item,
				})),
			];
		},
	},
	props: {
		feedbackId: {
			type: [String, Number],
			required: true,
		},
		candidate: {
			type: Object,
			required: true,
		},
		onClose: {
			type: Function,
			required: false,
			default() {
				return false;
			},
		},
	},
	data() {
		return {
			format,
			isVisible: false,
			isCompleteConfirmVisible: false,
			feedback: {
				json_schema: [],
				context_schema: {},
				overall_score: null,
				pipeline_status: null,
				job_id: null,
				application_id: null,
				instructions: null,
			},
		};
	},
	methods: {
		sanitize,
		async handleCompleteClick() {
			const isValid = await this.$refs.form_new_feedback.validate();
			if (isValid) {
				this.isCompleteConfirmVisible = true;
			}
		},
		handleConfirmedComplete(complete_and_share) {
			this.isCompleteConfirmVisible = false;
			this.handleSubmit(null, complete_and_share);
		},
		async handleGetFeedback({ id }) {
			this.$store
				.dispatch('feedbacks/get', { id, refresh: true })
				.then(response => {
					const { items } = response.data;
					if (items.length > 0) {
						this.feedback = items[0];
					}
				});
		},
		async handleSaveAsDraft() {
			const {
				id,
				context_schema,
				overall_score,
				pipeline_status,
				job_id,
				application_id,
			} = this.feedback;

			this.$store
				.dispatch('feedbacks/put', {
					items: [
						{
							id,
							context_schema,
							overall_score,
							pipeline_status,
							job_id,
							application_id,
							status: 'draft',
						},
					],
				})
				.then(() => {
					this.$store.dispatch('drafts/get_feedbacks', {});
					this.onClose({ needsRefresh: true });
				});
		},
		async handleSubmit(evt, complete_and_share) {
			if (evt) {
				evt.preventDefault();
			}

			const isValid = await this.$refs.form_new_feedback.validate();
			if (isValid) {
				const {
					id,
					context_schema,
					overall_score,
					pipeline_status,
					job_id,
					application_id,
				} = this.feedback;

				this.$store
					.dispatch('feedbacks/put', {
						items: [
							{
								id,
								context_schema,
								overall_score,
								pipeline_status,
								pipeline_status_translation: this.$t(
									`pipeline.steps.${pipeline_status}`,
								),
								job_id,
								application_id,
								status: 'completed',
								complete_and_share,
							},
						],
					})
					.then(() => {
						this.$store.dispatch('drafts/get_feedbacks', {});
						this.onClose({ needsRefresh: true });
					});
			}
		},
		handleDynamicFormChange({ newVal }) {
			this.feedback = {
				...this.feedback,
				context_schema: {
					...this.feedback.context_schema,
					...newVal,
				},
			};
		},
		handleClickOutside(evt) {
			evt.preventDefault();
		},
		handleFormatDate({ timestamp = new Date(), format = 'dd.MM.yyyy HH:mm' }) {
			const dateToFormat = new Date(timestamp);
			return this.format(dateToFormat, format);
		},
		handleApplicationIdChange({ applicationId }) {
			if (applicationId) {
				const application = this.candidate.applications.find(
					item => item.id === applicationId,
				);
				const { job_id } = application;
				const getPipelineStatus = ({
					is_archived,
					archive_status,
					is_interview,
					interview_status,
					is_lead,
					lead_status,
				}) => {
					if (is_archived) {
						return archive_status ? archive_status : 'underqualified';
					} else if (is_interview) {
						return interview_status ? interview_status : 'phone';
					} else if (is_lead) {
						return lead_status ? lead_status : 'new-lead';
					}
					return 'new-applicant';
				};
				const pipeline_status = getPipelineStatus(application);
				this.feedback = {
					...this.feedback,
					pipeline_status,
					job_id,
				};
			}
		},
	},
	mounted() {
		setTimeout(
			function () {
				this.isVisible = true;
			}.bind(this),
			250,
		);
	},
	watch: {
		feedbackId: {
			handler: function (newVal) {
				this.handleGetFeedback({ id: newVal });
			},
			deep: true,
			immediate: true,
		},
	},
};
</script>

<style lang="scss">
.v-input--selection-controls {
	&.overall-score-selection-controls {
		margin-top: 0;
	}
}
</style>
